<template>
  <Teleport
    id="dev-ValidationErrorDialog"
    to="body"
  >
    <transition name="modal-fade">
      <div
        v-if="errorData"
        class="modal-backdrop"
        @click="handleClose"
      >
        <div
          class="modal-content"
          @click.stop
        >
          <ModalCloseButton
            class="validation-error-close"
            @close="handleClose"
          />
          <!-- Cable Selection Error - Use UnifiedCableSelector -->
          <div v-if="errorConfig.showCableSelection && availableCables.length > 0">
            <SelectCableType
              :user-id="user ? user.id : null"
              @cable-type="confirmCableSelection"
            />
          </div>

          <!-- Other Errors -->
          <div
            v-else
            class="modal-body-container"
          >
            <!-- Error Icon -->
            <div
              class="error-icon-wrapper"
              :class="getErrorIconClass(errorConfig.iconClass)"
            >
              <font-awesome-icon
                :icon="errorConfig.icon"
                class="error-icon"
              />
            </div>

            <!-- Error Title -->
            <h5 class="modal-title text-center">
              {{ $t(errorConfig.titleKey) }}
            </h5>

            <!-- Error Message -->
            <div class="error-message text-center">
              {{ errorMessage }}
            </div>

            <!-- Nearby Terminals (NO_CHARGED_BATTERY) -->
            <div
              v-if="errorConfig.showNearbyTerminals && nearbyTerminals.length > 0"
              class="nearby-terminals-section"
            >
              <h6 class="section-title">
                {{ $t('map_navigation.error_nearby_terminals_title') }}
              </h6>
              <div class="terminals-list">
                <div
                  v-for="terminal in nearbyTerminals"
                  :key="terminal.point_id"
                  class="terminal-card"
                  @click="handleSelectNearbyTerminal(terminal)"
                >
                  <div class="terminal-info">
                    <div class="terminal-name">
                      {{ terminal.name }}
                    </div>
                    <div class="terminal-details">
                      <span class="terminal-distance">
                        <font-awesome-icon
                          icon="location-dot"
                          class="me-1"
                        />
                        {{ $t('map_navigation.error_nearby_terminal_distance', { distance: terminal.distanceMeters }) }}
                      </span>
                      <span class="terminal-batteries">
                        <font-awesome-icon
                          icon="battery-three-quarters"
                          class="me-1"
                        />
                        {{ terminal.available_batteries }}
                      </span>
                    </div>
                  </div>
                  <div class="terminal-action">
                    <font-awesome-icon icon="chevron-right" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Terminal Information (Generic Errors) -->
            <div
              v-if="!errorConfig.showCableSelection && !errorConfig.showNearbyTerminals && terminalInfo"
              class="terminal-info-section"
            >
              <h6 class="section-title">
                {{ $t('map_navigation.validation_error_terminal_info') }}
              </h6>
              <div class="terminal-info-card">
                <div class="info-row">
                  <div class="info-icon">
                    <font-awesome-icon icon="fa-solid fa-store" />
                  </div>
                  <div class="info-body">
                    <span class="info-label">{{ $t('map_navigation.station_name') }}</span>
                    <span class="info-value">{{ terminalInfo.point_name }}</span>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-icon">
                    <font-awesome-icon icon="fa-solid fa-key" />
                  </div>
                  <div class="info-body">
                    <span class="info-label">{{ $t('map_navigation.terminal_token') }}</span>
                    <span class="info-value">{{ terminalInfo.token }}</span>
                  </div>
                </div>

                <div
                  v-if="terminalInfo.address"
                  class="info-row"
                >
                  <div class="info-icon">
                    <font-awesome-icon icon="fa-solid fa-location-dot" />
                  </div>
                  <div class="info-body">
                    <span class="info-label">{{ $t('map_navigation.address') }}</span>
                    <span class="info-value">{{ terminalInfo.address }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div
              class="actions-section"
              :class="{ 'multi-actions': errorConfig.secondaryActionKey }"
            >
              <!-- Retry Action -->
              <BaseButton
                v-if="errorConfig.showRetry"
                variant="primary"
                unstyled
                @click="handleRetry"
              >
                <font-awesome-icon icon="rotate-right" />
                {{ $t(errorConfig.primaryActionKey) }}
              </BaseButton>

              <!-- Default Primary Action -->
              <BaseButton
                v-else
                variant="primary"
                unstyled
                @click="errorConfig.primaryActionEmit === 'secondary' ? handleSecondaryAction() : handleClose()"
              >
                {{ $t(errorConfig.primaryActionKey) }}
              </BaseButton>

              <!-- Secondary Action -->
              <BaseButton
                v-if="errorConfig.secondaryActionKey"
                variant="outline-secondary"
                unstyled
                @click="errorConfig.secondaryActionEmit === 'close' ? handleClose() : handleSecondaryAction()"
              >
                {{ $t(errorConfig.secondaryActionKey) }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
  import { getErrorType, getErrorConfig } from '../utils/errorHandlers'
  import SelectCableType from '@/components/SelectCableType.vue'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'
  import { lockScroll, unlockScroll } from '@/utils/scrollLock'

  export default {
    name: 'ValidationErrorDialog',
    components: {
      SelectCableType,
      BaseButton,
      ModalCloseButton
    },
    props: {
      errorData: {
        type: Object,
        required: true,
        default: () => ({})
      },
      nearbyTerminals: {
        type: Array,
        default: () => []
      },
      isValidating: {
        type: Boolean,
        default: false
      },
      user: {
        type: Object,
        default: null
      }
    },
    emits: ['close', 'change-cable', 'retry', 'select-terminal', 'select-cable', 'secondary-action'],
    computed: {
      errorType() {
        // Safety check: if no error code, treat as generic error
        if (!this.errorData || !this.errorData.code) {
          return 'generic'
        }
        return getErrorType(this.errorData.code)
      },
      errorConfig() {
        return getErrorConfig(this.errorType)
      },
      availableCables() {
        // Backend returns BATTERY types (each battery has 2 cables):
        // 0 = Lightning + Micro USB
        // 1 = Lightning + USB-C
        // 2 = e-Bike
        //
        // Frontend uses INDIVIDUAL cable types:
        // 0 = Lightning
        // 1 = Micro USB
        // 2 = USB-C
        //
        // We need to convert battery types to individual cable options

        const batteryTypes = this.errorData.cable_type_available || []
        const availableCableSet = new Set()

        batteryTypes.forEach(batteryType => {
          switch (batteryType) {
            case 0: // Lightning + Micro USB battery
              availableCableSet.add(0) // Lightning
              availableCableSet.add(1) // Micro USB
              break
            case 1: // Lightning + USB-C battery
              availableCableSet.add(0) // Lightning
              availableCableSet.add(2) // USB-C
              break
            case 2: // e-Bike
              // e-Bike não é exibido na seleção padrão
              break
          }
        })

        return Array.from(availableCableSet).sort()
      },
      terminalInfo() {
        return this.errorData.extra?.terminal || null
      },
      errorMessage() {
        // If errorConfig has a messageKey defined use it
        if (this.errorConfig.messageKey) {
          return this.$t(this.errorConfig.messageKey)
        }

        // If errorData has an error message, use it
        if (this.errorData.error) {
          // Check if it looks like a translation key by detecting the pattern
          if (typeof this.errorData.error === 'string' && this.errorData.error.includes('.')) {
            // Try to translate it, fallback to generic error if not found
            const translated = this.$t(this.errorData.error)
            // If translation return the key itself, it means key was not found
            // In that case, use a generic error message
            return translated === this.errorData.error ? this.$t('map_navigation.generic_error_message') : translated
          }
          return this.errorData.error
        }

        // Ultimate fallback
        return this.$t('map_navigation.generic_error_message')
      }
    },
    mounted() {
      lockScroll()
    },
    beforeUnmount() {
      unlockScroll()
    },
    methods: {
      handleClose() {
        this.$emit('close')
      },
      handleSecondaryAction() {
        this.$emit('secondary-action', {
          errorType: this.errorType,
          errorCode: this.errorData?.code ?? null
        })
      },
      handleRetry() {
        this.$emit('retry')
      },
      handleSelectNearbyTerminal(terminal) {
        this.$emit('select-terminal', terminal)
      },
      confirmCableSelection(cableType) {
        // When UnifiedCableSelector emits select-cable, forward it
        this.$emit('select-cable', cableType)
      },
      getErrorIconClass(originalClass) {
        if (originalClass && originalClass.includes('text-warning')) return 'bg-warning-light'
        if (originalClass && originalClass.includes('text-danger')) return 'bg-danger-light'
        return 'bg-info-light'
      }
    }
  }
</script>

<style scoped lang="scss">
  .validation-error-close {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
  }

  .modal-backdrop {
    position: fixed;
    top: 0;
    bottom: var(--app-fixed-bottom-offset, 0px);
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 12000; // Higher than CheckoutFlowModal (11000)
    padding: 1rem;
  }

  .modal-content {
    position: relative;
    background: #ffffff;
    border-radius: 16px;
    max-width: 400px;
    width: 100%;
    max-height: calc(var(--app-viewport-safe-height, 100vh) - 16px);
    max-height: calc(var(--app-viewport-safe-height, 100dvh) - 16px);
    overflow-y: auto;
    padding: 0;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    animation: modal-pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  @keyframes modal-pop {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  .modal-body-container {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
  }

  .error-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin: 0 auto 1.25rem;

    &.bg-warning-light {
      background-color: #fff3cd;
      color: #856404;
    }

    &.bg-danger-light {
      background-color: #f8d7da;
      color: #721c24;
    }

    &.bg-info-light {
      background-color: #d1ecf1;
      color: #0c5460;
    }
  }

  .error-icon {
    font-size: 28px;
  }

  .modal-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 0.75rem;
    line-height: 1.3;
  }

  .error-message {
    font-size: 0.95rem;
    color: #5a6c7d;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  .section-title {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    color: #95a5a6;
    margin-bottom: 0.75rem;
  }

  // Nearby Terminals Section
  .nearby-terminals-section {
    background-color: #f8f9fa;
    border-radius: 12px;
    padding: 1rem;
    border: 1px solid #e9ecef;
    margin-bottom: 1.5rem;
  }

  .terminals-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .terminal-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      border-color: #ced4da;
    }
  }

  .terminal-info {
    flex: 1;
  }

  .terminal-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.35rem;
  }

  .terminal-details {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.75rem;
    color: #7f8c8d;
  }

  .terminal-action {
    color: #3498db;
    font-size: 0.875rem;
    margin-left: 0.75rem;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ebf5fb;
    border-radius: 50%;
  }

  // Terminal Info Section
  .terminal-info-section {
    margin-bottom: 1.5rem;
  }

  .terminal-info-card {
    background-color: #f8f9fa;
    border-radius: 12px;
    padding: 1rem;
    border: 1px solid #e9ecef;
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-icon {
    width: 32px;
    height: 32px;
    background: white;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3498db;
    font-size: 0.9rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    flex-shrink: 0;
  }

  .info-body {
    display: flex;
    flex-direction: column;
  }

  .info-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #95a5a6;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: 0.85rem;
    color: #2c3e50;
    font-weight: 600;
    line-height: 1.3;
  }

  // Actions Section
  .actions-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: auto;

    &.multi-actions {
      flex-direction: row-reverse;

      .btn {
        flex: 1;
      }
    }
  }

  .btn {
    border-radius: 12px;
    padding: 0.65rem 1rem;
    font-size: var(--fs-sm);
    font-weight: var(--fw-semibold);
    letter-spacing: -0.01em;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: inherit;

    &:active {
      transform: scale(0.98);
    }
  }

  .btn-primary {
    background: #f58a48;
    color: white;
    box-shadow: 0 4px 12px rgba(245, 138, 72, 0.3);

    &:hover {
      background: #e67e3c;
      box-shadow: 0 6px 15px rgba(245, 138, 72, 0.4);
      transform: translateY(-1px);
    }
  }

  .btn-outline-secondary {
    background: white;
    color: #7f8c8d;
    border: 2px solid #ecf0f1;

    &:hover {
      background: #f8f9fa;
      color: #34495e;
      border-color: #bdc3c7;
    }
  }

  // Responsive
  @media (max-width: 576px) {
    .modal-backdrop {
      padding: 0.5rem;
    }

    .modal-content {
      max-height: 95vh;
    }

    .modal-body-container {
      padding: 1.25rem;
    }

    .error-icon-wrapper {
      width: 56px;
      height: 56px;
      margin-bottom: 1rem;
    }

    .error-icon {
      font-size: 24px;
    }

    .modal-title {
      font-size: 1.1rem;
    }

    .btn {
      padding: 0.6rem;
      font-size: 0.85rem;
    }
  }
</style>
